import { z } from 'zod/v4';

/** What the admin credit-transactions list sorts by. */
export const ADMIN_CREDIT_TRANSACTIONS_SORT_FIELDS = [
  'createdAt',
  'amount',
  'type',
] as const;
export type AdminCreditTransactionsSortField =
  (typeof ADMIN_CREDIT_TRANSACTIONS_SORT_FIELDS)[number];

/** Query of `GET /api/admin/companies/:id/credits`. */
export const AdminCompanyCreditsQuerySchema = z.object({
  /** 0-based. */
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(100).default(10),
  /**
   * `<field>:ASC|DESC`, any case; newest first without it. An unknown field
   * sorts newest first too.
   */
  sort: z
    .string()
    .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
    .optional(),
});
export type AdminCompanyCreditsQuery = z.infer<
  typeof AdminCompanyCreditsQuerySchema
>;

/** One credit movement of a company: a top-up, or credits an analysis used. */
export const AdminCreditTransactionSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  /** The analysis that consumed the credits, for a consumption. */
  analysisId: z.string().nullable(),
  type: z.string(),
  amount: z.number(),
  balanceBefore: z.number().nullable(),
  balanceAfter: z.number().nullable(),
  /** The email of the user who caused it; `null` without one. */
  userEmail: z.string().nullable(),
  description: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type AdminCreditTransaction = z.infer<
  typeof AdminCreditTransactionSchema
>;

export const AdminCompanyCreditsResponseSchema = z.array(
  AdminCreditTransactionSchema,
);

/** `GET /api/admin/companies/:id/credits/balance`: all 0 before any top-up. */
export const AdminCompanyCreditBalanceSchema = z.object({
  /** Credits available to spend. */
  balance: z.number(),
  /** Credits held for analyses still running. */
  reserved: z.number(),
  /** Credits spent so far. */
  consumed: z.number(),
});
export type AdminCompanyCreditBalance = z.infer<
  typeof AdminCompanyCreditBalanceSchema
>;

/** Body of `POST /api/admin/companies/:id/credits`. */
export const AdminCompanyCreditTopUpBodySchema = z.object({
  /** Credits to add; more than 0, fractions allowed. */
  amount: z.number().positive(),
  /** Shown on the ledger row, up to 500 characters. */
  description: z.string().max(500).optional(),
});
export type AdminCompanyCreditTopUpBody = z.infer<
  typeof AdminCompanyCreditTopUpBodySchema
>;

/** The balance after a top-up. */
export const AdminCompanyCreditTopUpResponseSchema = z.object({
  balance: z.number(),
});
